/**
 * match.ts — Match state management
 *
 * Tracks teams, soldiers, turns, scores, and victory conditions.
 * Pure data + logic, no rendering or DOM dependencies.
 */

import {
  computeTrajectory,
  checkHit,
  type TrajectoryPoint,
  type FieldBounds,
  DEFAULT_FIELD_BOUNDS,
} from './trajectory';
import {
  Obstacle,
  ObstacleDensity,
  generateObstacles,
  checkObstacleCollision,
  applyBite,
} from './obstacles';

/** A single soldier on the field */
export interface Soldier {
  id: string;
  x: number;
  y: number;
  alive: boolean;
  teamIndex: number;
  isBot: boolean;
}

/** A team of soldiers */
export interface Team {
  name: string;
  color: string;
  soldiers: Soldier[];
}

/** Match configuration */
export interface MatchConfig {
  /** Number of soldiers per team */
  team1Count: number;
  team2Count: number;
  /** Field bounds */
  bounds: FieldBounds;
  /** Hit radius for collision detection */
  hitRadius: number;
  /** Obstacle density */
  obstacleDensity?: ObstacleDensity;
  showCoordinates: boolean;
  turnTimer: number; // 0 for off, else seconds
  chatEquations: boolean;
}

/** Result of applying a turn */
export interface TurnResult {
  /** The trajectory that was fired */
  trajectory: TrajectoryPoint[];
  /** Soldiers that were killed this turn */
  kills: Soldier[];
  /** The soldier who fired */
  shooter: Soldier;
  /** Whether the match is now over */
  matchOver: boolean;
  /** Winning team index, or -1 if not over */
  winnerIndex: number;
}

/** Complete match state */
export interface MatchState {
  teams: Team[];
  /** Index into a flat list of all soldiers, indicating whose turn it is */
  currentTurnIndex: number;
  /** Flat ordered list of soldier IDs for turn rotation */
  turnOrder: string[];
  config: MatchConfig;
  obstacles: Obstacle[];
  /** Is the match finished? */
  finished: boolean;
  /** Index of winning team, or -1 */
  winnerIndex: number;
  /** Turn counter */
  turnNumber: number;
}

/** Default match config */
export const DEFAULT_MATCH_CONFIG: MatchConfig = {
  team1Count: 2,
  team2Count: 2,
  bounds: DEFAULT_FIELD_BOUNDS,
  hitRadius: 0.5,
  obstacleDensity: 'none',
  showCoordinates: false,
  turnTimer: 60,
  chatEquations: true,
};

/**
 * Generates random positions for soldiers in their designated zone (left or right half).
 */
function generatePositions(
  count: number,
  bounds: FieldBounds,
  side: 'left' | 'right',
  minSpacing: number = 4.0,
  existing: Array<{ x: number; y: number }> = [],
): Array<{ x: number; y: number }> {
  const positions: Array<{ x: number; y: number }> = [...existing];
  const newPositions: Array<{ x: number; y: number }> = [];

  const xRange = side === 'left'
    ? { min: bounds.minX + 3, max: -3 }
    : { min: 3, max: bounds.maxX - 3 };
  const yRange = { min: bounds.minY + 3, max: bounds.maxY - 3 };

  for (let i = 0; i < count; i++) {
    let attempts = 0;
    let pos: { x: number; y: number };
    do {
      pos = {
        x: xRange.min + Math.random() * (xRange.max - xRange.min),
        y: yRange.min + Math.random() * (yRange.max - yRange.min),
      };
      attempts++;
    } while (
      attempts < 200 &&
      positions.some(p => Math.hypot(p.x - pos.x, p.y - pos.y) < minSpacing)
    );

    const rounded = {
      x: Math.round(pos.x * 10) / 10,
      y: Math.round(pos.y * 10) / 10,
    };
    positions.push(rounded);
    newPositions.push(rounded);
  }

  return newPositions;
}

/**
 * Creates a new match state.
 */
export function createMatch(config: MatchConfig = DEFAULT_MATCH_CONFIG): MatchState {
  const team1Positions = generatePositions(config.team1Count, config.bounds, 'left', 6.0);
  const team2Positions = generatePositions(config.team2Count, config.bounds, 'right', 6.0, team1Positions);

  const allSoldierPositions = [...team1Positions, ...team2Positions];
  const obstacles = generateObstacles(
    config.obstacleDensity || 'none',
    config.bounds,
    allSoldierPositions,
  );

  const teams: Team[] = [
    {
      name: 'Team 1',
      color: '#1D4E89',
      soldiers: team1Positions.map((pos, i) => ({
        id: `t0-s${i}`,
        x: pos.x,
        y: pos.y,
        alive: true,
        teamIndex: 0,
        isBot: i > 0, // First soldier is human, rest of Team 1 are bots
      })),
    },
    {
      name: 'Team 2',
      color: '#d94040',
      soldiers: team2Positions.map((pos, i) => ({
        id: `t1-s${i}`,
        x: pos.x,
        y: pos.y,
        alive: true,
        teamIndex: 1,
        isBot: true, // All Team 2 members are bots
      })),
    },
  ];

  // Turn order: alternate between teams until all soldiers listed
  const turnOrder: string[] = [];
  const maxCount = Math.max(config.team1Count, config.team2Count);
  for (let i = 0; i < maxCount; i++) {
    if (i < config.team1Count) turnOrder.push(teams[0].soldiers[i].id);
    if (i < config.team2Count) turnOrder.push(teams[1].soldiers[i].id);
  }

  return {
    teams,
    currentTurnIndex: 0,
    turnOrder,
    config,
    obstacles,
    finished: false,
    winnerIndex: -1,
    turnNumber: 1,
  };
}

/**
 * Gets the soldier whose turn it currently is.
 */
export function getCurrentSoldier(state: MatchState): Soldier {
  const id = state.turnOrder[state.currentTurnIndex];
  for (const team of state.teams) {
    for (const soldier of team.soldiers) {
      if (soldier.id === id) return soldier;
    }
  }
  throw new Error(`Soldier ${id} not found`);
}

/**
 * Gets a soldier by ID.
 */
export function getSoldierById(state: MatchState, id: string): Soldier | undefined {
  for (const team of state.teams) {
    for (const soldier of team.soldiers) {
      if (soldier.id === id) return soldier;
    }
  }
  return undefined;
}

/**
 * Determines optimal direction for a shooter based on nearest alive enemy position.
 */
export function getShooterDirection(state: MatchState, shooter: Soldier): 1 | -1 {
  return shooter.teamIndex === 0 ? 1 : -1;
}

/**
 * Applies a turn: computes trajectory, checks for kills, advances turn.
 *
 * @param state - Current match state (will be mutated)
 * @param fn - The compiled function evaluator
 * @returns TurnResult with trajectory, kills, and match status
 */
export function applyTurn(
  state: MatchState,
  fn: (x: number) => number,
): TurnResult {
  const shooter = getCurrentSoldier(state);

  // Direction: Team 0 shoots right (+1), Team 1 shoots left (-1)
  const direction: 1 | -1 = shooter.teamIndex === 0 ? 1 : -1;

  // Compute full trajectory
  const rawTrajectory = computeTrajectory(
    fn,
    shooter.x,
    shooter.y,
    direction,
    state.config.bounds,
  );

  let effectiveTrajectory = rawTrajectory;

  // Check obstacle collision (ignoring shooter's immediate position)
  const obs = state.obstacles || [];
  const obsHit = checkObstacleCollision(rawTrajectory, obs, shooter.x, shooter.y);
  if (obsHit) {
    // Truncate trajectory at the obstacle circumference
    effectiveTrajectory = rawTrajectory.slice(0, obsHit.hitIndex + 1);
    effectiveTrajectory.push(obsHit.hitPoint);
    // Carve bite out of obstacle at impact point
    applyBite(obsHit.obstacle, obsHit.hitPoint.x, obsHit.hitPoint.y, 0.65);
  }

  // Check for hits against all alive soldiers (except the shooter)
  const kills: Soldier[] = [];
  for (const team of state.teams) {
    for (const soldier of team.soldiers) {
      if (!soldier.alive || soldier.id === shooter.id) continue;

      const hitIndex = checkHit(
        effectiveTrajectory,
        soldier.x,
        soldier.y,
        state.config.hitRadius,
      );

      if (hitIndex >= 0) {
        soldier.alive = false;
        kills.push(soldier);
      }
    }
  }

  // Check victory condition
  const winnerIndex = checkVictory(state);
  if (winnerIndex >= 0) {
    state.finished = true;
    state.winnerIndex = winnerIndex;
  }

  // Advance to next alive soldier's turn
  if (!state.finished) {
    advanceTurn(state);
  }

  state.turnNumber++;

  return {
    trajectory: effectiveTrajectory,
    kills,
    shooter,
    matchOver: state.finished,
    winnerIndex: state.winnerIndex,
  };
}

/**
 * Advances the turn to the next alive soldier.
 */
function advanceTurn(state: MatchState): void {
  const total = state.turnOrder.length;
  let next = (state.currentTurnIndex + 1) % total;

  // Find next alive soldier
  let safety = 0;
  while (safety < total) {
    const soldier = getSoldierById(state, state.turnOrder[next]);
    if (soldier && soldier.alive) {
      state.currentTurnIndex = next;
      return;
    }
    next = (next + 1) % total;
    safety++;
  }
}

/**
 * Checks if a team has won (all opponents eliminated).
 *
 * @returns The winning team index, or -1 if no winner yet
 */
export function checkVictory(state: MatchState): number {
  const aliveByTeam = state.teams.map(team =>
    team.soldiers.filter(s => s.alive).length
  );

  // If a team has 0 alive soldiers, the other team wins
  for (let i = 0; i < aliveByTeam.length; i++) {
    if (aliveByTeam[i] === 0) {
      // Return the other team's index
      return i === 0 ? 1 : 0;
    }
  }

  return -1;
}

/**
 * Gets the count of alive soldiers per team.
 */
export function getAliveCount(state: MatchState): number[] {
  return state.teams.map(team =>
    team.soldiers.filter(s => s.alive).length
  );
}
